import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { useToast } from '../context/ToastContext';
import { Eye, EyeOff } from 'lucide-react';

const changePasswordSchema = z.object({
  currentPassword: z.string().min(8, 'Current password is required'),
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one digit')
    .regex(/[^a-zA-Z0-9\s]/, 'Password must contain at least one symbol'),
  confirmPassword: z.string().min(8, 'Please confirm your new password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

export function ChangePasswordComponent() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      // First verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: data.currentPassword,
      });

      if (signInError) {
        if (signInError.message.includes('Invalid login credentials')) {
          showToast('The current password you entered is incorrect. Please try again.', 'error');
          return;
        } else {
          showToast('Unable to verify current password. Please try again.', 'error');
          return;
        }
      }

      // Validate new password
      if (data.newPassword === data.currentPassword) {
        showToast('New password must be different from your current password', 'error');
        return;
      }

      // Update password in Auth
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.newPassword,
      });

      if (updateError) {
        setError(updateError.message);
        showToast('Failed to update password. Please try again later.', 'error');
        return;
      }

      showToast('Your password has been successfully updated', 'success');
      reset(); // Clear form
    } catch (error) {
      console.error('Failed to change password:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again later.');
      showToast(
        'An unexpected error occurred. Please try again later.',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="bg-[--bg-primary] shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-[--text-primary]">
            Change Password
          </h3>
          <div className="mt-2 max-w-xl text-sm text-[--text-secondary]">
            <p>Update your password to keep your account secure.</p>
          </div>

          {error && (
            <div className="mt-4 p-4 text-sm text-warning-700 bg-warning-100 rounded-lg dark:bg-[rgb(var(--warning-900)/0.2)] dark:text-warning-200" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4">
            {/* Current Password */}
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-[--text-primary]">
                Current Password
              </label>
              <div className="mt-1">
                <div className="relative">
                  <input
                    {...register('currentPassword')}
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="appearance-none block w-full px-3 py-2 border border-[--border-secondary] rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showCurrentPassword ? <EyeOff className="h-5 w-5 text-[--text-disabled]" /> : <Eye className="h-5 w-5 text-[--text-disabled]" />}
                  </button>
                </div>
                {errors.currentPassword && (
                  <p className="mt-2 text-sm text-error-600">{errors.currentPassword.message}</p>
                )}
              </div>
            </div>

            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-[--text-primary]">
                New Password
              </label>
              <div className="mt-1">
                <div className="relative">
                  <input
                    {...register('newPassword')}
                    type={showNewPassword ? 'text' : 'password'}
                    className="appearance-none block w-full px-3 py-2 border border-[--border-secondary] rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showNewPassword ? <EyeOff className="h-5 w-5 text-[--text-disabled]" /> : <Eye className="h-5 w-5 text-[--text-disabled]" />}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="mt-2 text-sm text-error-600">{errors.newPassword.message}</p>
                )}
              </div>
              <div className="mt-2 text-sm text-[--text-secondary]">
                Password must contain:
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li>At least 8 characters</li>
                  <li>At least one lowercase letter</li>
                  <li>At least one uppercase letter</li>
                  <li>At least one number</li>
                  <li>At least one symbol</li>
                </ul>
              </div>
            </div>

            {/* Confirm New Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-[--text-primary]">
                Confirm New Password
              </label>
              <div className="mt-1">
                <div className="relative">
                  <input
                    {...register('confirmPassword')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="appearance-none block w-full px-3 py-2 border border-[--border-secondary] rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5 text-[--text-disabled]" /> : <Eye className="h-5 w-5 text-[--text-disabled]" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-2 text-sm text-error-600">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isSubmitting ? 'Changing password...' : 'Change password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}